import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown as _CrownUnused } from "lucide-react";
import CommunityIQLogo from "@/components/CommunityIQLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (e, p) => { setEmail(e); setPassword(p); };

  return (
    <div className="min-h-screen bg-background paper-texture flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center justify-center mb-4" data-testid="login-brand-link">
          <CommunityIQLogo className="w-48 h-48 -my-8" />
        </Link>

        <div className="hoa-card p-8 royal-ring">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to your community dashboard.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full h-11 rounded-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground mb-3">Quick demo accounts:</p>
            <div className="grid gap-2">
              <button
                type="button"
                onClick={() => quickFill("admin@hoa.com", "admin123")}
                className="text-left text-xs px-3 py-2 rounded-md bg-accent/50 hover:bg-accent transition-colors"
                data-testid="demo-admin-btn"
              >
                Admin · admin@hoa.com / admin123
              </button>
              <button
                type="button"
                onClick={() => quickFill("board@hoa.com", "board123")}
                className="text-left text-xs px-3 py-2 rounded-md bg-accent/50 hover:bg-accent transition-colors"
                data-testid="demo-board-btn"
              >
                Board · board@hoa.com / board123
              </button>
              <button
                type="button"
                onClick={() => quickFill("owner@hoa.com", "owner123")}
                className="text-left text-xs px-3 py-2 rounded-md bg-accent/50 hover:bg-accent transition-colors"
                data-testid="demo-owner-btn"
              >
                Owner · owner@hoa.com / owner123
              </button>
            </div>
          </div>

          <p className="text-sm text-center text-muted-foreground mt-6">
            No account yet?{" "}
            <Link to="/register" className="text-gold font-medium hover:underline" data-testid="go-to-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
